import React, { useState, useEffect } from 'react'
import { TextInputOld, Button } from '@doseme/cohesive-ui'
import noop from 'lodash/noop'
import { isLoading, isLoaded } from 'flux-entities'
import moment from 'moment'

import { IInstallation } from '../../../../types/installation'
import { TProps } from './types'
import { INSTALLATIONS_URL } from '../../../../constants/urls/frontend'
import { FormPageLayout } from '../../../../components/FormPageLayout'
import { StyledForm } from '../../../../components/StyledForm'
import { TFormsValidity } from '../../types'

type TKey = 'description' | 'uri' | 'code'

const Edit: React.FC<TProps> = (props) => {
  const { installations, fetchInstallations, updateInstallation, history, match } = props

  const [copy, updateCopy] = useState<IInstallation>({
    id: 0,
    code: '',
    description: '',
    uri: '',
    hospitalCount: 0,
    created: moment(),
    updated: moment()
  })
  const [error, updateError] = useState('')

  const [fieldsValidity, setFieldsValidity] = useState<TFormsValidity<TKey>>({
    description: {
      touched: true,
      valid: true
    },
    uri: {
      touched: true,
      valid: true
    },
    code: {
      touched: true,
      valid: true
    }
  })

  useEffect(() => {
    if (!installations.touched) {
      fetchInstallations()
    }
  }, [installations.touched, fetchInstallations])

  useEffect(() => {
    const loadedInstallation = installations.all[match.params.id]
    if (loadedInstallation) {
      updateCopy(loadedInstallation)
    }
  }, [installations.all, updateCopy, match.params.id])

  const installation = installations.all[match.params.id]

  if (!installation && isLoaded(installations)) {
    return <div>No installation was found with the id {match.params.id}</div>
  }

  if (copy.id === 0) {
    return <div>Loading...</div>
  }

  const handleInput = (name: string, value: string, valid: boolean) => {
    if (valid) {
      updateCopy({ ...copy, [name]: value })
    }

    setFieldsValidity({
      ...fieldsValidity,
      [name]: {
        touched: true,
        valid
      }
    })
  }

  const handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    updateError('')

    const { status, message } = await updateInstallation({ ...copy, updated: moment() })
    if (status >= 400) {
      updateError(message)
      return
    }

    history.push(INSTALLATIONS_URL)
  }

  const formValid = (Object.keys(fieldsValidity) as TKey[]).every(
    (field) => fieldsValidity[field].valid && fieldsValidity[field].touched
  )

  return (
    <FormPageLayout
      title='Edit Installation'
      subtitle={`${installation.description} – ${installation.code}`}
      handleBackClick={() => history.push(INSTALLATIONS_URL)}
    >
      <StyledForm handleSubmit={handleSubmit}>
        <TextInputOld label='Code' name='code' type='text' defaultValue={copy.code} onChange={noop} readOnly disabled />
        <br />

        <TextInputOld
          label='Description'
          name='description'
          data-test='edit-description'
          type='text'
          defaultValue={copy.description}
          isRequired
          onBlur={(value, valid) => handleInput('description', value, valid)}
        />
        <br />

        <TextInputOld
          label='URI'
          name='uri'
          type='text'
          data-test='edit-uri'
          defaultValue={copy.uri}
          isRequired
          onBlur={(value, valid) => handleInput('uri', value, valid)}
        />
        <br />
        <hr />

        <div className='d-flex w-100 justify-content-end'>
          <Button
            type='submit'
            variant='primary'
            onClick={noop}
            disabled={!formValid}
            loading={isLoading(installations)}
          >
            Update
          </Button>
        </div>
        {error}
      </StyledForm>
    </FormPageLayout>
  )
}

export { Edit }
